export type AdministrationTimeOptions = Readonly<{
  locale?: string;
  timeZone?: string;
}>;

function resolvedOptions(options?: AdministrationTimeOptions): Required<AdministrationTimeOptions> {
  return {
    locale: options?.locale ?? globalThis.navigator.language,
    timeZone: options?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function zoneLabel(timeZone: string): string {
  if (timeZone === "Europe/London") return "UK time";
  if (timeZone === "UTC" || timeZone === "Etc/UTC") return "UTC";
  return `local time (${timeZone.replaceAll("_", " ")})`;
}

function formatter(options: Required<AdministrationTimeOptions>): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(options.locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: options.timeZone,
    hour12: false,
  });
}

export function formatAdministrationTime(
  epochMilliseconds: number,
  options?: AdministrationTimeOptions,
): string {
  const resolved = resolvedOptions(options);
  return `${formatter(resolved).format(new Date(epochMilliseconds))} ${zoneLabel(resolved.timeZone)}`;
}

export function formatAdministrationWindow(
  fromEpochMilliseconds: number,
  toEpochMilliseconds: number,
  options?: AdministrationTimeOptions,
): string {
  const resolved = resolvedOptions(options);
  const format = formatter(resolved);
  return `${format.format(new Date(fromEpochMilliseconds))} to ${format.format(new Date(toEpochMilliseconds))} ${zoneLabel(resolved.timeZone)}`;
}

export function exactUtcTimestamp(epochMilliseconds: number): string {
  return new Date(epochMilliseconds).toISOString();
}

export function parseAdministrationLocalDateTime(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  const [, yearText, monthText, dayText, hourText, minuteText] = match;
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const local = new Date(year, monthIndex, day, hour, minute, 0, 0);
  if (
    local.getFullYear() !== year ||
    local.getMonth() !== monthIndex ||
    local.getDate() !== day ||
    local.getHours() !== hour ||
    local.getMinutes() !== minute
  ) {
    return null;
  }
  return local.getTime();
}
